import React, { PropsWithChildren } from 'react';
import { getClonedChild, Group, useBreakpoint } from '@landr/maestro';
import { useModalContext } from 'contexts/ModalContext/ModalContext';
import { InlineTrending } from './components/InlineTrending/InlineTrending';
import SearchBar from './components/SearchBar/SearchBar';
import SearchBarProvider from './context/SearchBarContext/SearchBarContext';
import { Suggestions } from './context/SearchBarContext/types';
import SearchTools from './components/SearchTools/SearchTools';
import { SuggestionsPills } from './components/SuggestionsPills/SuggestionsPills';
import AdvancedFiltersModal from './components/SearchTools/AdvancedFiltersModal';

export const SearchV3ToolsWidth = '500px';

export enum searchV3FilterType {
    Types = 'types',
    BPM = 'bpm',
    Key = 'key',
}

interface SubComponents {
    SearchBar: typeof SearchBar;
    SuggestionsPills: typeof SuggestionsPills;
    InlineTrending: typeof InlineTrending;
}

const Filters: React.FC<
    PropsWithChildren<{
        defaultPlaceholder?: string;
        trending?: Suggestions[]; // TODO: remove once search v3 is fully released
        withAdvancedFilters?: boolean;
        showOnlyFilterSuggestions?: boolean;
        isRecentAllowed?: boolean;
        isTrendingAllowed?: boolean;
    }>
> &
    SubComponents = ({
    children,
    defaultPlaceholder,
    trending,
    withAdvancedFilters = true,
    showOnlyFilterSuggestions = true,
    isRecentAllowed = false,
    isTrendingAllowed = false,
}) => {
    const childrenArray = React.Children.toArray(children);
    const isMobile = useBreakpoint('md');
    const { closeModal, isModalVisible } = useModalContext();

    const searchBar = getClonedChild(childrenArray, Filters.SearchBar);
    const suggestionsPills = getClonedChild(
        childrenArray,
        Filters.SuggestionsPills,
    );
    const inlineTrending = getClonedChild(
        childrenArray,
        Filters.InlineTrending,
    );

    return (
        <SearchBarProvider
            defaultPlaceholder={defaultPlaceholder}
            showOnlyFilterSuggestions={showOnlyFilterSuggestions}
            isRecentAllowed={isRecentAllowed}
            isTrendingAllowed={isTrendingAllowed}
        >
            <Group
                direction="vertical"
                gap="none"
                marginBottom="xl"
                width="100%"
            >
                <Group.Item>
                    <Group
                        direction={isMobile ? 'vertical' : 'horizontal'}
                        maxWidth={'100%'}
                        gap="xs"
                        width="100%"
                    >
                        <Group.Item width="100%">{searchBar}</Group.Item>
                        <Group.Item
                            flexGrow={10}
                            style={{
                                width: isMobile ? '100%' : SearchV3ToolsWidth,
                            }}
                            display={!withAdvancedFilters ? 'none' : 'flex'}
                        >
                            <SearchTools
                                isMobile={isMobile}
                                withAdvancedFilters={withAdvancedFilters}
                            />
                        </Group.Item>
                    </Group>
                </Group.Item>
                {inlineTrending}
                {suggestionsPills}
                <AdvancedFiltersModal
                    isOpen={isModalVisible.advancedFiltersModal}
                    handleClose={closeModal.advancedFiltersModal}
                />
            </Group>
        </SearchBarProvider>
    );
};

Filters.SearchBar = SearchBar;
Filters.SuggestionsPills = SuggestionsPills;
Filters.InlineTrending = InlineTrending;

export default Filters;
